import React from 'react';
import {Box, Chip, IconButton, Tooltip, Typography} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useGridApiContext} from '@mui/x-data-grid-pro';

export const CATEGORIES = [
  {key: 'unmapped', label: 'Unmapped', description: 'Herd pen holding cows with no pen config'},
  {key: 'feed_link', label: 'Feed link', description: 'Feed pen blank, or no delivery to it in 14 days'},
  {key: 'out_of_production', label: 'Out of production', description: 'Milk to Processor pen that is not mostly lactating'},
  {key: 'into_production', label: 'Into production', description: 'Not Milking or blank pen that is mostly lactating'},
  {key: 'stage_mismatch', label: 'Stage mismatch', description: 'Pen stage label contradicts the animals in it'},
  {key: 'unused_pen', label: 'Unused pen', description: 'Milk to Processor pen holding no animals (never High)'}
];

const CATEGORY_LABELS = Object.fromEntries(CATEGORIES.map((c) => [c.key, c.label]));
const CATEGORY_DESCRIPTIONS = Object.fromEntries(CATEGORIES.map((c) => [c.key, c.description]));

const PARENT_UNMAPPED = {
  label: 'Not assigned to a site',
  description: 'Herd pen on a multi-site parent that no child site claims; map it on the right site'
};

const categoryLabel = (row) => (
  row.is_multi_site_parent && row.category === 'unmapped' ? PARENT_UNMAPPED.label : CATEGORY_LABELS[row.category] || row.category
);

const categoryDescription = (row) => (
  row.is_multi_site_parent && row.category === 'unmapped' ? PARENT_UNMAPPED.description : CATEGORY_DESCRIPTIONS[row.category] || ''
);

const formatDate = (isoDate) => (isoDate
  ? new Date(`${isoDate}T00:00:00`).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})
  : '');

const NONE = <Typography variant='body2' sx={{color: 'text.disabled'}}>–</Typography>;

const Tag = ({label, title}) => (
  <Tooltip title={title}>
    <Chip size='small' label={label} variant='outlined' sx={{height: 18, fontSize: '0.65rem', color: 'text.secondary'}}/>
  </Tooltip>
);

export const MultiSiteParentTag = () => (
  <Tag label='PARENT' title='Parent of a multi-site farm. Its own alerts are herd pens not assigned to any of its sites.'/>
);

export const StrayTag = ({peak}) => (
  <Tag label='STRAY' title={`Never held more than ${peak} animals in 90 days; likely a data-entry stray, so kept low`}/>
);

export const StaleTag = () => (
  <Tag label='STALE' title='No herd data for today; evaluated on this older snapshot'/>
);

export const columnGroupingModel = [
  {
    groupId: 'issues',
    headerName: 'Alerts by pen issue',
    description: 'Flagged pens per issue. Bold when any of them are High.',
    headerAlign: 'center',
    children: CATEGORIES.map(({key}) => ({field: key}))
  }
];

const farmPathKey = (row) => `farm:${row.multi_site_owner_id}`;

export const getTreeDataPath = (row) => {
  if (!row.multi_site_owner_id) return [row.business_id];
  return row.is_multi_site_parent ? [farmPathKey(row)] : [farmPathKey(row), row.business_id];
};

const FarmHeading = ({name, siteCount}) => (
  <Box sx={{py: '0.4rem'}}>
    <Box sx={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
      <Typography variant='body2'>{name}</Typography>
      <MultiSiteParentTag/>
    </Box>
    <Typography variant='caption' sx={{color: 'text.secondary'}}>{siteCount} sites · no herd pens left on the parent</Typography>
  </Box>
);

const BusinessTreeCell = ({id, row, rowNode}) => {
  const apiRef = useGridApiContext();
  const isGroup = rowNode.type === 'group';
  const toggle = (event) => {
    event.stopPropagation();
    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
  };
  return (
    <Box sx={{display: 'flex', alignItems: 'center', pl: rowNode.depth * 4}}>
      <Box sx={{width: 36, flexShrink: 0}}>
        {isGroup && (
          <IconButton size='small' onClick={toggle} aria-label={rowNode.childrenExpanded ? 'Collapse sites' : 'Expand sites'}>
            {rowNode.childrenExpanded ? <ExpandMoreIcon fontSize='small'/> : <ChevronRightIcon fontSize='small'/>}
          </IconButton>
        )}
      </Box>
      {row.business_id
        ? <BusinessCell row={row}/>
        : <FarmHeading name={apiRef.current.getRow(rowNode.children[0])?.multi_site_name} siteCount={rowNode.children.length}/>}
    </Box>
  );
};

export const businessGroupingColDef = {
  headerName: 'Business',
  description: 'Business name, then owner id · cows · herd snapshot date. Multi-site farms group their sites under the parent.',
  width: 340,
  sortable: false,
  renderCell: (params) => <BusinessTreeCell {...params}/>
};

const BusinessCell = ({row}) => (
  <Box sx={{py: '0.4rem'}}>
    <Box sx={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
      <Typography variant='body2'>{row.business_name}</Typography>
      {row.is_multi_site_parent && <MultiSiteParentTag/>}
      {row.is_stale && <StaleTag/>}
    </Box>
    <Typography variant='caption' sx={{color: 'text.secondary'}}>
      {row.owner_id} · {Number(row.cow_count).toLocaleString()} cows · {formatDate(row.snapshot_date)}
    </Typography>
  </Box>
);

const AlertsCell = ({row}) => {
  if (!row.high_count && !row.low_count) return NONE;
  return (
    <Box sx={{py: '0.4rem'}}>
      <Typography variant='body2' component='div'>
        {row.high_count > 0 && <Box component='span' sx={{color: 'error.main', fontWeight: 700}}>{row.high_count} High</Box>}
        {row.high_count > 0 && row.low_count > 0 && <Box component='span' sx={{color: 'text.secondary'}}> · </Box>}
        {row.low_count > 0 && <Box component='span' sx={{color: 'text.secondary'}}>{row.low_count} low</Box>}
      </Typography>
      {row.oldest_high_flagged_since && (
        <Typography variant='caption' sx={{color: 'text.secondary'}}>since {formatDate(row.oldest_high_flagged_since)}</Typography>
      )}
    </Box>
  );
};

const IssueCell = ({row, category}) => {
  const high = row[`${category}_high_count`] || 0;
  const low = row[`${category}_low_count`] || 0;
  if (!high && !low) return NONE;
  const notAssigned = category === 'unmapped' && row.is_multi_site_parent ? ' not assigned to a site' : '';
  const parts = [high && `${high} High`, low && `${low} low`].filter(Boolean).join(' · ') + notAssigned;
  return (
    <Tooltip title={parts}>
      <Typography variant='body2' sx={high ? {fontWeight: 700} : {color: 'text.secondary'}}>{high + low}</Typography>
    </Tooltip>
  );
};

export const buildBusinessColumns = () => ([
  {
    field: 'alerts',
    headerName: 'Pen alerts',
    description: 'High and low alerts across all pen issues, and how long the oldest High has been open',
    minWidth: 170,
    flex: 1.2,
    valueGetter: ({row}) => (row.high_count || 0) * 1000 + (row.low_count || 0),
    renderCell: ({row}) => <AlertsCell row={row}/>
  },
  ...CATEGORIES.map(({key, label, description}) => ({
    field: key,
    headerName: label,
    description,
    minWidth: 130,
    flex: 1,
    align: 'center',
    headerAlign: 'center',
    valueGetter: ({row}) => (row[`${key}_high_count`] || 0) * 1000 + (row[`${key}_low_count`] || 0),
    renderCell: ({row}) => <IssueCell row={row} category={key}/>
  }))
]);

const muted = {color: 'text.secondary'};

const Code = ({children}) => (
  <Box component='span' sx={{fontWeight: 700, whiteSpace: 'pre'}}>{`"${children}"`}</Box>
);

const largestGroup = (row) => {
  const groups = [['lactating cows', row.lactating_count], ['dry cows', row.dry_count], ['young stock', row.young_count]];
  return groups.reduce((best, group) => (group[1] > best[1] ? group : best));
};

const makeup = (row) => `${row.lactating_count} lactating · ${row.dry_count} dry · ${row.young_count} young stock`;

const FeedLinkDetail = ({row}) => {
  const suggestion = row.suggested_feed_pen && <> Did you mean <Code>{row.suggested_feed_pen}</Code>?</>;
  switch (row.feed_link_reason) {
    case 'no_feed_pen':
      return <>No feed pen is set for this pen, so its feed can't be matched.</>;
    case 'stale':
      return <>Feed pen <Code>{row.src_feed_pen_id}</Code> hasn't had a delivery since {formatDate(row.last_feed_delivery)}.{suggestion}</>;
    case 'name_mismatch':
      return <>Feed pen <Code>{row.src_feed_pen_id}</Code> differs from the feed system's <Code>{row.suggested_feed_pen}</Code> only by case or spacing.</>;
    default:
      return (
        <>
          Feed pen <Code>{row.src_feed_pen_id}</Code> isn't in the feed data.
          {suggestion || (row.recent_feed_pen_count != null
            ? ` No close match among the farm's ${row.recent_feed_pen_count} recently fed pens.`
            : ' No close match among the farm\'s recently fed pens.')}
        </>
      );
  }
};

const IssueDetail = ({row}) => {
  switch (row.category) {
    case 'unmapped':
      return row.is_multi_site_parent
        ? <>{row.cow_count} cows in a herd pen no site claims. Add the pen to the right site's pen config.</>
        : <>{row.cow_count} cows in a herd pen with no pen config.</>;
    case 'feed_link':
      return <FeedLinkDetail row={row}/>;
    case 'out_of_production':
      return <>Milk to Processor, but only {row.lactating_count} of {row.animal_count} animals are lactating ({makeup(row)}).</>;
    case 'into_production':
      return <>{row.milk_disposition || 'No disposition set'}, but {row.lactating_count} of {row.animal_count} animals are lactating.</>;
    case 'stage_mismatch':
      return <>Labelled {row.animal_stage}, but most animals are {largestGroup(row)[0]} ({makeup(row)}).</>;
    case 'unused_pen':
      return <>Milk to Processor pen with no animals in it.</>;
    default:
      return null;
  }
};

export const buildFlagColumns = () => ([
  {field: 'src_pen_id', headerName: 'Pen', width: 80},
  {
    field: 'severity',
    headerName: 'Severity',
    width: 140,
    renderCell: ({row}) => (
      <Box sx={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
        <Tooltip title={row.severity === 'high' && row.base_severity === 'low' ? 'Escalated: low for 7+ days' : ''}>
          <Typography
            variant='body2'
            sx={row.severity === 'high' ? {color: 'error.main', fontWeight: 700} : muted}
          >
            {row.severity === 'high' ? 'High' : 'low'}
          </Typography>
        </Tooltip>
        {row.is_stray_pen && row.category !== 'unused_pen' && <StrayTag peak={row.peak_animal_count_90d}/>}
      </Box>
    )
  },
  {
    field: 'category',
    headerName: 'Issue',
    width: 190,
    valueGetter: ({row}) => categoryLabel(row),
    renderCell: ({row, value}) => (
      <Tooltip title={categoryDescription(row)}>
        <Typography variant='body2'>{value}</Typography>
      </Tooltip>
    )
  },
  {
    field: 'detail',
    headerName: "What's wrong",
    minWidth: 420,
    flex: 1,
    sortable: false,
    renderCell: ({row}) => (
      <Typography variant='body2' sx={{py: '0.4rem', whiteSpace: 'normal'}}><IssueDetail row={row}/></Typography>
    )
  },
  {
    field: 'days_flagged',
    headerName: 'Days',
    type: 'number',
    width: 70,
    renderCell: ({row}) => (
      <Tooltip title={`Flagged since ${row.flagged_since}`}>
        <Typography variant='body2'>{row.days_flagged}{row.is_flagged_since_window_start ? '+' : ''}</Typography>
      </Tooltip>
    )
  },
  {field: 'animal_stage', headerName: 'Pen stage', width: 170, cellClassName: 'pen-health-muted'},
  {field: 'milk_disposition', headerName: 'Disposition', width: 150, cellClassName: 'pen-health-muted'},
  {field: 'src_feed_pen_id', headerName: 'Feed pen', width: 100, cellClassName: 'pen-health-muted'},
  {field: 'animal_count', headerName: 'Animals', type: 'number', width: 90, cellClassName: 'pen-health-muted'},
  {field: 'lactating_count', headerName: 'Lactating', type: 'number', width: 95, cellClassName: 'pen-health-muted'},
  {field: 'dry_count', headerName: 'Dry', type: 'number', width: 70, cellClassName: 'pen-health-muted'},
  {field: 'young_count', headerName: 'Young stock', type: 'number', width: 105, cellClassName: 'pen-health-muted'}
]);
